import Vue from 'vue';
import Vuex from 'vuex';

import { setGTagUser } from '@/lib/gtag.js';
import tourSteps from '@/lib/state/tourSteps';
import { Repository } from '@/db/_repo';
import { UserAnswers } from './models/UserAnswers';
import { DeckModel } from './models/deck';
import { DueDateStatsModel } from './models/DueDateStats';
import { orderBy } from 'lodash';
import { UserProfileModel, NotificationTypes } from './models/user';

const db = new Repository();

Vue.use(Vuex);

const state = {
    currentUser: null,
    userProfile: null,
    featureFlags: {
        // TODO: #314 
        showImportExport: false,
    },
    checkoutDefaultURL: '/account/checkout?sub=jnfr',
    tourRunning: false,
    ...tourSteps,
    privateDecks: [],
    sharedDecks: [],
    userAnswers: [],
    dueDateStats: [] as DueDateStatsModel[]
};


const mutations = {
    // tslint:disable no-shadowed-variable
    SET_USER(state: { currentUser: any; }, user: any) {
        state.currentUser = user;
    },
    SET_USER_PROFILE(state, userProfile: UserProfileModel) {
        state.userProfile = { ...userProfile };
    },  
    UPDATE_NOTIFICATION_PREFS(state, model: {category: string, type: NotificationTypes, checked: boolean}) {
        const newPrefs = { ...state.userProfile.notificationPreferences };
        const idx = newPrefs[model.category].indexOf(model.type);
        const isEnabled = idx > -1;

        if (model.checked && !isEnabled) {
            newPrefs[model.category].push(model.type);
            state.userProfile.NotificationPreferences = newPrefs;
        }
        if (!model.checked && isEnabled) {
            newPrefs[model.category].splice(idx, 1);
            state.userProfile.NotificationPreferences = newPrefs;
        }       
    },
    SWITCH_TOUR(state) {
        state.tourRunning = !state.tourRunning;
    },
    STOP_TOUR(state) {
        state.tourRunning = false;
    },
    UPDATE_DECKS(state, model: DeckModel) {
        if (model === null || model === undefined) {
            // we always want the state to be an array, even if empty - don't want it to change to null or some crazy thing
            return;
        }
        state.decks = model;
    },
    UPDATE_USER_ANSWERS(state, model: UserAnswers[]) {
        if (model === null || model === undefined) {
            // we always want the state to be an array, even if empty - don't want it to change to null or some crazy thing
            return;
        }
        state.userAnswers = model;
    },
    UPDATE_SINGLE_USER_ANSWER(state, model: UserAnswers) {
        // let currentAnswer = state.userAnswers.find(ua => ua.id === model.id);
        let currentAnswerIndex = state.userAnswers.findIndex(ua => ua.id === model.id);
        const newState = {...state};
        if (currentAnswerIndex !== -1) {
            // have to do it this way so its reactive
            newState.userAnswers.splice(currentAnswerIndex, 1, model);
            // Vue.set(state.userAnswers, currentAnswerIndex, currentAnswer);
            // currentAnswer = model;
        } else {
            newState.userAnswers.push(model);
        }
        // state.userAnswers = newState.userAnswers;
        // newState.userAnswers = [];
        state.userAnswers = newState.userAnswers;
    },
    // ***** DECKS ******
    ADD_PRIVATE_DECKS(state, model: DeckModel[]) {
        const newState = {...state};
        if (model !== null) {
            newState.privateDecks = newState.privateDecks.concat(model);
            this.state.privateDecks = newState.privateDecks;
        }
    },
    ADD_SHARED_DECKS(state, model: DeckModel[]) {

        const newState = {...state};

        if (model !== null) {
            newState.sharedDecks = newState.sharedDecks.concat(model);
            this.state.sharedDecks = newState.sharedDecks;
        }
    },
    UPDATE_SHARED_DECKS(state, model: DeckModel[]) {

        const newState = {...state};

        if (model !== null) {
            newState.sharedDecks = model;
            this.state.sharedDecks = newState.sharedDecks;
        }
        
    },
    UPDATE_SINGLE_PRIVATE_DECK(state, model: DeckModel) {
        // otherwise there is reference issues
        const newModel = {...model};
        const newState = {...state};
        const currentDeckIndex = newState.privateDecks.findIndex(deck => deck.id === newModel.id);

        if (currentDeckIndex !== -1) {
            newState.privateDecks.splice(currentDeckIndex, 1, newModel);
        } else {
            // TODO: log very strange error that should not happen ever
        }
        state.privateDecks = newState.privateDecks;
    },
    REMOVE_SINGLE_PRIVATE_DECK(state, deckId: string) {
        const newState = {...state};
        const currentDeckIndex = newState.privateDecks.findIndex(deck => deck.id === deckId);

        if (currentDeckIndex !== -1) {
            newState.privateDecks.splice(currentDeckIndex, 1);
        } else {
            // TODO: log very strange error that should not happen ever
        }
        state.privateDecks = newState.privateDecks;
    },
    // ***** DueDateStats ******
    UPDATE_DUE_DATE_STATS(state, model: DueDateStatsModel[]) {
        state.dueDateStats = model;
    }
};

const actions = {
    setUser({commit}: any, user: any) {
        db.users.write(user);
        if (user) setGTagUser(user.uid);
        commit('SET_USER', user);
    },
    async getUserProfile({commit}: any, userId: string) {
        const userProfile = await db.users.getProfileDataById(userId);
        commit('SET_USER_PROFILE', userProfile);
    },
    async setUserProfile({commit}: any, userProfile: UserProfileModel) {
        await db.users.updateUserProfile(userProfile);
        commit('SET_USER_PROFILE', userProfile);
    },
    updateNotificationPreferences({ commit, state }: any, model: {category: string, type: NotificationTypes, checked: boolean}) {
        const newPrefs = { ...state.userProfile.notificationPreferences };
        if (model.category === 'optOutAll') {
            newPrefs.optOutAll = model.checked;
        } else {
            const idx = newPrefs[model.category].indexOf(model.type);
            const isEnabled = idx > -1;
    
            if (model.checked && !isEnabled) {
                newPrefs[model.category].push(model.type);
            }
            if (!model.checked && isEnabled) {
                newPrefs[model.category].splice(idx, 1);
                state.userProfile.NotificationPreferences = newPrefs;
            } 
        }
        // TODO: handle errors better
        store.dispatch('setUserProfile', {...this.state.userProfile, notificationPreferences: newPrefs}); 
    },
    toggleTour({commit}: any) {
        commit('SWITCH_TOUR');
    },
    async setUserAnswer({ commit }: any, userAnswer: UserAnswers) {
        let thisUAId = userAnswer.id;
        if (userAnswer.id === undefined || userAnswer.id === null) {
            // add
            const answer = await db.decks.userAnswers.addAsync(userAnswer);
            thisUAId = answer.id;
            commit('UPDATE_SINGLE_USER_ANSWER', answer);
        } else {
            // update
            await db.decks.userAnswers.updateAsync(userAnswer);
            commit('UPDATE_SINGLE_USER_ANSWER', userAnswer);
        }
        
        return thisUAId;
    },
    async getAllPrivateDecks({commit}: any, user: any) {
        const decks = await db.decks.getUserDecksAsync(user.uid);
        commit('ADD_PRIVATE_DECKS', decks);
    },
    async getAllSharedDecks({commit}: any, user: any) {
        const decks = await db.decks.getUserSharedDecksAsync(user.uid);
        commit('UPDATE_SHARED_DECKS', decks);
    },
    async updateDeck({commit}: any, deck: DeckModel) {
        await db.decks.updateDeck(deck, deck.id);
        commit('UPDATE_SINGLE_PRIVATE_DECK', deck);
    },
    async removeDeck({commit}: any, removeModel: {deckId: string, userId: string}) {
        await db.decks.deleteDeck(removeModel.deckId, removeModel.userId);
        commit('REMOVE_SINGLE_PRIVATE_DECK', removeModel.deckId);
    },
    async setAllDueDeckStats({commit}: any, userId: string) {
        const stats = await db.dueDateStats.getByUserAsync(userId);
        commit('UPDATE_DUE_DATE_STATS', stats);
    }
};

const getters = {
    // tslint:disable no-shadowed-variable
    currentUser: state => state.currentUser,
    currentUserProfile: state => state.userProfile,
    allUserAnswers: state => state.userAnswers,
    dueUserAnswers: (state, getters) => { 
            return getters.allUserAnswers.filter(ua => {
                if (ua.nextIntervalDate) {
                    return ua.nextIntervalDate.toDate() <  Date.now();
                }
                // assume that it is not due if there is no nextIntervalDate
                return false;
            });
        },
    uaCount: (state, getters) => {
        return getters.allUserAnswers.length;
    },
    getUserAnswerByCardId: (state) => (cardId) => {
        return state.userAnswers.find(ua => ua.cardGuid === cardId);
    },
    allUserDecks: (state, getters) => {
        return state.privateDecks;
    },
    privateUserDecks: (state, getters) => {
        // for now we always return sorted, we can add another getter later if we want
        return state.privateDecks.length > 0 ? orderBy(state.privateDecks, [deck => deck.name.toLowerCase()] , ['asc']) : state.privateDecks;
    },
    sharedUserDecks: (state, getters) => {
        return state.sharedDecks.length > 0 ? orderBy(state.sharedDecks, [deck => deck.name.toLowerCase()] , ['asc']) : state.sharedDecks;
    },
    allDueDeckStats: state => state.dueDateStats,
};


const store = new Vuex.Store({
    // strict: true,
    state,
    mutations,
    actions,
    getters,
});

export default store;
